<template>
  <div>
    <div class="fin-summary-row">
      <div class="fin-summary-card" :class="{ 'is-loading': loading }">
        <div class="fin-summary-icon"><i class="fa fa-layer-group"></i></div>
        <div>
          <div class="fin-summary-value">
            <span v-if="loading && !totals.groups" class="fin-skeleton-line fin-skeleton-line--sm"></span>
            <template v-else>{{ totals.groups || 0 }}</template>
          </div>
          <div class="fin-summary-label">Grupe magazinas</div>
        </div>
      </div>
      <div class="fin-summary-card" :class="{ 'is-loading': loading }">
        <div class="fin-summary-icon"><i class="fa fa-cubes"></i></div>
        <div>
          <div class="fin-summary-value">
            <span v-if="loading && totals.qty == null" class="fin-skeleton-line fin-skeleton-line--md"></span>
            <template v-else>{{ formatQty(totals.qty) }}</template>
          </div>
          <div class="fin-summary-label">Sasia totale</div>
        </div>
      </div>
      <div class="fin-summary-card" :class="{ 'is-loading': loading }">
        <div class="fin-summary-icon"><i class="fa fa-coins"></i></div>
        <div>
          <div class="fin-summary-value">
            <span v-if="loading && totals.value == null" class="fin-skeleton-line fin-skeleton-line--md"></span>
            <template v-else>{{ formatValue(totals.value) }}</template>
          </div>
          <div class="fin-summary-label">Vlera totale ({{ valueLabel }})</div>
        </div>
      </div>
    </div>

    <div class="fin-table-wrap" :class="{ 'is-reloading': loading && groups.length }">
      <div class="fin-table-scroll">
        <table class="fin-table">
          <thead>
            <tr>
              <th class="fin-col-product">Grupi i magazinave</th>
              <th>Magazinat</th>
              <th>Produkte me stok</th>
              <th>Sasia</th>
              <th>Vlera</th>
              <th></th>
            </tr>
          </thead>
          <tbody v-if="loading && !groups.length">
            <tr v-for="n in 6" :key="'sk-' + n" class="fin-skeleton-row">
              <td class="fin-col-product"><div class="fin-skeleton-line fin-skeleton-line--lg"></div></td>
              <td style="text-align: center"><span class="fin-skeleton-line fin-skeleton-line--xs"></span></td>
              <td style="text-align: center"><span class="fin-skeleton-line fin-skeleton-line--xs"></span></td>
              <td style="text-align: center"><span class="fin-skeleton-line fin-skeleton-line--sm"></span></td>
              <td style="text-align: center"><span class="fin-skeleton-line fin-skeleton-line--sm"></span></td>
              <td></td>
            </tr>
          </tbody>
          <tbody v-else-if="!groups.length">
            <tr>
              <td colspan="6" class="fin-empty">Nuk ka grupe magazinash me të dhëna.</td>
            </tr>
          </tbody>
          <tbody v-else>
            <template v-for="group in groups" :key="group.guid || group.name">
              <tr>
                <td class="fin-col-product">
                  <div class="fin-product-name">{{ group.name }}</div>
                </td>
                <td style="text-align: center">{{ group.warehouse_count }}</td>
                <td style="text-align: center">{{ group.products }}</td>
                <td style="text-align: center">{{ formatQty(group.qty) }}</td>
                <td style="text-align: center">{{ formatValue(group.value) }}</td>
                <td style="text-align: center">
                  <button
                    v-if="group.guid"
                    type="button"
                    class="fin-link-btn"
                    @click="$emit('expand', group)"
                  >
                    {{ expandedGuid === group.guid ? "Mbyll" : "Detaje produkte" }}
                  </button>
                </td>
              </tr>
              <tr v-if="expandedGuid === group.guid && expanded">
                <td colspan="6" style="padding: 0; background: #f8fafc">
                  <div style="padding: 12px 16px">
                    <div v-if="expandLoading" class="fin-skeleton-row" style="padding: 12px 0">
                      <div class="fin-skeleton-line fin-skeleton-line--lg" style="margin-bottom: 8px"></div>
                      <div class="fin-skeleton-line fin-skeleton-line--md"></div>
                    </div>
                    <table v-else class="fin-table" style="margin: 0">
                      <thead>
                        <tr>
                          <th class="fin-col-product">Produkti</th>
                          <th>Sasia</th>
                          <th>Vlera</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="row in expanded.rows || []" :key="row.code">
                          <td class="fin-col-product">
                            <div class="fin-product-name-row">
                              <div class="fin-product-name">{{ row.name }}</div>
                              <button
                                v-if="canOpenProduct && row.guid"
                                type="button"
                                class="fin-product-kartela-btn"
                                title="Hap kartelën e produktit"
                                @click.stop="$emit('open-product', row)"
                              >
                                <i class="fa fa-pencil"></i>
                              </button>
                            </div>
                            <div class="fin-product-meta">{{ row.code }}</div>
                          </td>
                          <td style="text-align: center">{{ formatQty(row.total && row.total.qty) }}</td>
                          <td style="text-align: center">{{ formatValue(row.total && row.total.value) }}</td>
                        </tr>
                        <tr v-if="!(expanded.rows || []).length">
                          <td colspan="3" class="fin-empty">Asnjë produkt në këtë grup.</td>
                        </tr>
                      </tbody>
                    </table>
                    <pagination-bar
                      v-if="expanded.pagination && expanded.pagination.last_page > 1"
                      :pagination="expanded.pagination"
                      @page="$emit('expand-page', $event)"
                      @per-page="$emit('expand-per-page', $event)"
                    />
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <div v-if="loading && groups.length" class="fin-table-overlay" aria-hidden="true"></div>
      </div>
    </div>
  </div>
</template>

<script>
import formatMixin from "../shared/formatMixin.js";
import PaginationBar from "./PaginationBar.vue";

export default {
  name: "FinancaDashboard",
  mixins: [formatMixin],
  components: { PaginationBar },
  props: {
    groups: { type: Array, default: () => [] },
    totals: { type: Object, default: () => ({}) },
    expanded: { type: Object, default: null },
    expandedGuid: { type: String, default: null },
    loading: { type: Boolean, default: false },
    expandLoading: { type: Boolean, default: false },
    valueLabel: { type: String, default: "sasia × kosto" },
    canOpenProduct: { type: Boolean, default: false },
  },
  emits: ["expand", "expand-page", "expand-per-page", "open-product"],
};
</script>
